import { useState, Dispatch, SetStateAction, FormEvent } from 'react'
import { Row, Switch, Alert } from 'antd'
import { Form } from '@ant-design/compatible'
import '@ant-design/compatible/assets/index.css'
import {
    FormComponentProps,
    WrappedFormUtils,
} from '@ant-design/compatible/lib/form/Form'
import style from '@ProdWb/components/ExceptionFailureModal/styles.css'
import { JobRecordType } from '@ProdWb/JobList/types'

import Box from '~/core/Box'
import Text from '~/core/Text'
import {
    ExceptionCreator,
    ExceptionFailureQuery,
    JobRequestStatus,
    useJobRequestsQuery,
    ExceptionFailureType,
    CorrectiveActionRequestStatus,
} from '~/gql/graphql'

import { NoteAttachmentType, PartsWithFailedQty } from './types'
import ExceptionActionsSection from './components/ExceptionActionsSection'
import ExceptionFailureDetailsSection from './components/ExceptionFailureDetailsSection'
import ExceptionDetailsSection from './components/ExceptionDetailsSection'
import { EXCEPTION_FORM_FIELDS } from './constants'

type ExceptionFormPropsType = FormComponentProps & {
    handleSubmit: (
        form: WrappedFormUtils,
        noteAttachment: NoteAttachmentType[],
        exceptionAttachments: NoteAttachmentType[],
        selectedParts: PartsWithFailedQty[],
    ) => void
    confirmFileUpload: (param: string) => void
    jobData?: JobRecordType
    exceptionId?: string
    setRestrictExceptionCreation: Dispatch<SetStateAction<boolean>>
    setRestrictExceptionOnRFI: Dispatch<SetStateAction<boolean>>
    exception?: ExceptionFailureQuery['exceptionFailure']
    exceptionLoading: boolean
}

function ExceptionForm(props: ExceptionFormPropsType) {
    const {
        form,
        handleSubmit,
        confirmFileUpload,
        form: { getFieldDecorator },
        jobData,
        exceptionId,
        setRestrictExceptionCreation,
        setRestrictExceptionOnRFI,
        exception,
        exceptionLoading,
    } = props

    const [partsWithQty, setPartsWithQty] = useState<PartsWithFailedQty[]>([])
    const [noteAttachment, setNoteAttachment] = useState<NoteAttachmentType[]>(
        [],
    )
    const [exceptionFailureAttachments, setExceptionFailureAttachments] =
        useState<NoteAttachmentType[]>([])

    const { data: jobRequestData, loading } = useJobRequestsQuery({
        variables: {
            jobId: (jobData?.id || exception?.jobId) as number,
            statuses: [
                JobRequestStatus.accepted,
                JobRequestStatus.canceled,
                JobRequestStatus.completed,
            ],
        },
        skip: !jobData?.id && !exception?.jobId,
    })

    const handleExceptionSubmit = (evt: Event | FormEvent) => {
        evt.preventDefault()

        handleSubmit(
            form,
            noteAttachment,
            exceptionFailureAttachments,
            partsWithQty || [],
        )
    }

    return (
        <Box direction="col">
            {exception?.type === ExceptionFailureType.OT &&
                exception.creator === ExceptionCreator.auto && (
                    <Alert
                        type="warning"
                        message="This is a automated OT exception created by the system, exception details cannot be modified."
                        style={{ marginTop: '16px' }}
                    />
                )}
            <Box direction="col" paddingBottom="16px">
                <Text className={style.modalSubtitleStyle}>
                    Exception details
                </Text>
            </Box>
            <Form
                id="exceptionForm"
                onSubmit={handleExceptionSubmit}
                colon={false}
                hideRequiredMark
                onClick={event => event.stopPropagation()}
            >
                {(jobData?.id || exception?.id) && !exceptionLoading && (
                    <ExceptionDetailsSection
                        form={form}
                        exception={exception}
                        saleOrderId={
                            jobData?.saleOrderId || exception?.erpSaleOrderId
                        }
                        jobRequests={jobRequestData?.jobRequests || []}
                        setRestrictExceptionCreation={
                            setRestrictExceptionCreation
                        }
                        setRestrictExceptionOnRFI={setRestrictExceptionOnRFI}
                        jobId={jobData?.id}
                        jobRequestLoading={loading}
                        partsWithQty={partsWithQty}
                        setPartsWithQty={setPartsWithQty}
                    />
                )}
                <Text fontSize="20px" fontWeight="bolder">
                    Exception actions
                </Text>
                <ExceptionActionsSection
                    form={form}
                    exception={exception}
                    confirmFileUpload={confirmFileUpload}
                    setNoteAttachment={setNoteAttachment}
                    noteAttachment={noteAttachment}
                />
                <Form.Item>
                    <Row className={style.switchStyle}>
                        {getFieldDecorator(EXCEPTION_FORM_FIELDS.FAILURE, {
                            initialValue: exception?.isFailure,
                            valuePropName: 'checked',
                        })(
                            <Switch
                                disabled={
                                    form.getFieldValue(
                                        EXCEPTION_FORM_FIELDS.TYPE,
                                    ) === ExceptionFailureType.RFI ||
                                    (!!exception?.correctiveActionRequestId &&
                                        exception.correctiveActionRequest
                                            ?.status !==
                                            CorrectiveActionRequestStatus.pending)
                                }
                                style={{ marginTop: '6px' }}
                            />,
                        )}
                        <Text
                            fontSize="16px"
                            color="blackAlpha85"
                            style={{ marginLeft: '4px' }}
                        >
                            Escalate to failure
                        </Text>
                    </Row>
                </Form.Item>
                {form.getFieldValue(EXCEPTION_FORM_FIELDS.FAILURE) === true && (
                    <ExceptionFailureDetailsSection
                        form={form}
                        exception={exception}
                        confirmFileUpload={confirmFileUpload}
                        exceptionId={exceptionId}
                        setExceptionAttachment={setExceptionFailureAttachments}
                        exceptionAttachment={exceptionFailureAttachments}
                    />
                )}
            </Form>
        </Box>
    )
}

export default Form.create<ExceptionFormPropsType>()(ExceptionForm)
